/**
 * Auth Redux Slice (Server-Side Session Architecture)
 *
 * All auth operations go through the backend API.
 * The frontend never touches Supabase auth directly.
 * Session is maintained via HttpOnly session_id cookie.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { authApi, AuthApiError, AuthUser, AuthRole } from '../../api/auth.service.js';
import type { RootState } from '../index.js';

/**
 * - `idle`        — nothing attempted yet
 * - `loading`     — a login/register is in flight
 * - `succeeded`   — we have a verified user
 * - `failed`      — the server positively told us we are not authenticated
 * - `unavailable` — we could not reach the server to find out. NOT a logout.
 *
 * `unavailable` exists because collapsing it into `failed` is what logs
 * everyone out on every deploy: for the few seconds DigitalOcean is swapping
 * containers, GET /auth/me can't be answered, and treating that silence as
 * "you are logged out" throws away a session that is still perfectly valid.
 */
type AuthStatus = 'idle' | 'loading' | 'succeeded' | 'failed' | 'unavailable';

interface AuthState {
  user: AuthUser | null;
  role: AuthRole | null;
  permissions: string[];
  status: AuthStatus;
  error: Error | null;
}

const initialState: AuthState = {
  user: null,
  role: null,
  permissions: [],
  status: 'idle',
  error: null,
};

/**
 * Phase 2 — cold-boot offline for the FIELD PWA (card 3zHfmFL2PJGm).
 *
 * Redux auth is deliberately in-memory (the real session is the HttpOnly cookie).
 * That means a cold restart WITH NO NETWORK leaves `user` null, GET /auth/me
 * comes back 'unavailable', and ProtectedRoute shows the reconnecting screen —
 * so a warehouse worker can never even reach the offline PIN pad.
 *
 * Fix: after a SUCCESSFUL online /auth/me we stash a MINIMAL office identity
 * (only what ProtectedRoute + field gating read: user, role, permissions). On a
 * later boot, if /auth/me is 'unavailable' AND we're in field context AND a
 * stashed identity exists, we hydrate Redux from it so the field app mounts
 * offline. When the network returns, /auth/me reconciles — a real 401 clears it.
 *
 * STRICTLY gated to the field PWA: the desktop/office app must NEVER trust a
 * cached identity, so we only persist/hydrate when booted at a /field/* path
 * (mirrors client/field/offline-queue/persistFieldQueries.ts isFieldAppContext).
 */
const FIELD_IDENTITY_KEY = 'lithos.fieldIdentity';

interface PersistedFieldIdentity {
  user: AuthUser;
  role: AuthRole | null;
  permissions: string[];
}

/** True only when the document was booted inside the field PWA scope. */
function isFieldContext(): boolean {
  if (typeof window === 'undefined') {return false;}
  return window.location.pathname.startsWith('/field');
}

function persistFieldIdentity(identity: PersistedFieldIdentity): void {
  if (typeof window === 'undefined' || !isFieldContext()) {return;}
  try {
    window.localStorage.setItem(FIELD_IDENTITY_KEY, JSON.stringify(identity));
  } catch {
    // Quota / private-mode — non-fatal; offline cold-boot just won't be available.
  }
}

function readPersistedFieldIdentity(): PersistedFieldIdentity | null {
  if (typeof window === 'undefined' || !isFieldContext()) {return null;}
  try {
    const raw = window.localStorage.getItem(FIELD_IDENTITY_KEY);
    if (!raw) {return null;}
    const parsed = JSON.parse(raw) as PersistedFieldIdentity;
    if (parsed && parsed.user && typeof parsed.user.id === 'string') {return parsed;}
    return null;
  } catch {
    return null;
  }
}

/**
 * Drop the persisted field identity. Called on a real (non-transient) sign-out
 * answer and on explicit field logout (client/field/pages/SettingsPage.tsx), so
 * a signed-out tablet can't cold-boot into a stale identity.
 */
export function clearPersistedFieldIdentity(): void {
  if (typeof window === 'undefined') {return;}
  try {
    window.localStorage.removeItem(FIELD_IDENTITY_KEY);
  } catch {
    // Ignore — nothing we can do, and the in-memory state is authoritative.
  }
}

/**
 * Backoff schedule for retrying GET /auth/me when the server is unreachable.
 * Totals ~15s, which comfortably covers a DigitalOcean container swap without
 * leaving someone staring at a spinner if the backend is genuinely down.
 */
const AUTH_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

const sleep = (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Fetch the current user, retrying only failures that carry no information
 * about auth state (network errors, 5xx, gateway errors). A 401 is an answer
 * and is returned immediately — retrying it would just delay the login screen.
 */
async function fetchCurrentUserWithRetry() {
  let lastError: unknown;

  for (let attempt = 0; attempt <= AUTH_RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await authApi.getCurrentUser();
    } catch (error) {
      lastError = error;
      const transient = error instanceof AuthApiError && error.isTransient;
      if (!transient) {throw error;}
      if (attempt < AUTH_RETRY_DELAYS_MS.length) {
        console.warn(
          `[auth] /auth/me unreachable (attempt ${attempt + 1}/${AUTH_RETRY_DELAYS_MS.length + 1}), retrying`,
        );
        await sleep(AUTH_RETRY_DELAYS_MS[attempt]);
      }
    }
  }

  throw lastError;
}

/**
 * Initialize auth on page load.
 * Calls GET /auth/me which validates the session cookie.
 *
 * Three outcomes, and keeping them distinct is the whole point:
 *   - a user object  → authenticated
 *   - null           → the server said we are not authenticated
 *   - rejected with 'unavailable' → we never got an answer; the existing
 *     session (an HttpOnly cookie the client can't even see) is untouched, so
 *     we must not act as though the user logged out.
 */
export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchCurrentUserWithRetry();
    } catch (error) {
      if (error instanceof AuthApiError && error.isTransient) {
        return rejectWithValue('unavailable' as const);
      }
      // No valid session — user is not authenticated
      return null;
    }
  }
);

/**
 * Login with email/password.
 * Backend sets session_id cookie automatically.
 */
export const loginWithEmail = createAsyncThunk(
  'auth/loginWithEmail',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.login(email, password);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

/**
 * Register with email/password.
 */
export const registerWithEmail = createAsyncThunk(
  'auth/registerWithEmail',
  async ({ email, password, fullName }: { email: string; password: string; fullName?: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.signup(email, password, fullName);
      if (response.needsVerification) {
        // User needs to verify email — not logged in yet
        return null;
      }
      // Auto-logged in — fetch full user data
      const meResponse = await authApi.getCurrentUser();
      return meResponse;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

/**
 * Logout — deactivate session, clear cookie.
 */
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await authApi.logout();
      return null;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Logout failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<AuthUser | null>) => {
      state.user = action.payload;
      state.status = action.payload ? 'succeeded' : 'idle';
      if (action.payload) {state.error = null;}
    },
    setRole: (state, action: PayloadAction<AuthRole | null>) => {
      state.role = action.payload;
    },
    setPermissions: (state, action: PayloadAction<string[]>) => {
      state.permissions = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.role = null;
      state.permissions = [];
      state.status = 'idle';
      state.error = null;
    },
    clearErrors: (state) => {
      state.error = null;
    },
    // Backward compatibility: setSession is a no-op now (sessions are server-side)
    setSession: () => {},
  },
  extraReducers: (builder) => {
    // Initialize auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        // Don't show loading flash — keep current status
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user;
          state.role = action.payload.role;
          state.permissions = action.payload.permissions;
          state.status = 'succeeded';
          // Field PWA only: stash a minimal identity for offline cold-boot.
          persistFieldIdentity({
            user: action.payload.user,
            role: action.payload.role,
            permissions: action.payload.permissions,
          });
        } else {
          // The server positively answered "not authenticated" — drop any
          // cached field identity so a signed-out tablet can't cold-boot into it.
          state.user = null;
          state.role = null;
          state.permissions = [];
          state.status = 'failed';
          clearPersistedFieldIdentity();
        }
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        if (action.payload === 'unavailable') {
          // Couldn't reach the backend. Say so, and leave whatever user data we
          // already had in place — the session cookie is still in the browser
          // and is very likely still valid.
          state.status = 'unavailable';
          // Cold-boot offline in the field PWA: no in-memory user yet, but a
          // previously-synced identity lets ProtectedRoute mount the field app
          // (and reach the offline PIN pad) instead of the reconnecting screen.
          // /auth/me reconciles once the network returns.
          if (!state.user) {
            const persisted = readPersistedFieldIdentity();
            if (persisted) {
              state.user = persisted.user;
              state.role = persisted.role;
              state.permissions = persisted.permissions;
            }
          }
          return;
        }
        state.status = 'failed';
        state.user = null;
        state.role = null;
        state.permissions = [];
      });

    // Login
    builder
      .addCase(loginWithEmail.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginWithEmail.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user;
          state.role = action.payload.role;
          state.permissions = action.payload.permissions;
          state.status = 'succeeded';
          state.error = null;
        } else {
          state.status = 'failed';
        }
      })
      .addCase(loginWithEmail.rejected, (state, action) => {
        state.status = 'failed';
        state.user = null;
        state.error = new Error(action.payload as string);
      });

    // Register
    builder
      .addCase(registerWithEmail.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(registerWithEmail.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user;
          state.role = action.payload.role;
          state.permissions = action.payload.permissions;
          state.status = 'succeeded';
        } else {
          state.status = 'idle'; // Needs email verification
        }
      })
      .addCase(registerWithEmail.rejected, (state, action) => {
        state.status = 'failed';
        state.error = new Error(action.payload as string);
      });

    // Logout
    builder
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.role = null;
        state.permissions = [];
        state.status = 'idle';
        state.error = null;
        clearPersistedFieldIdentity();
      })
      .addCase(logoutUser.rejected, (state, action) => {
        // Force clear even on error
        state.user = null;
        state.role = null;
        state.permissions = [];
        state.status = 'idle';
        state.error = new Error(action.payload as string);
        clearPersistedFieldIdentity();
      });
  },
});

export const { setUser, setRole, setPermissions, clearAuth, clearErrors, setSession } = authSlice.actions;

// Selectors
export const selectUser = (state: RootState) => state.auth.user;
export const selectRole = (state: RootState) => state.auth.role;
export const selectPermissions = (state: RootState) => state.auth.permissions;
export const selectAuthStatus = (state: RootState) => state.auth.status;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectIsAuthenticated = (state: RootState) => Boolean(state.auth.user);
export const selectIsAdmin = (state: RootState) => state.auth.role?.isAdmin || false;
export const selectSession = (state: RootState) => null; // Deprecated — sessions are server-side

export default authSlice.reducer;
